'use client';
// SportsMarketDetails Component - Sports match market details page
import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CommentsSection, ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';

interface SportsMarketDetailsProps {
  marketId: string;
  team1: {
    id: string;
    name: string;
    logo?: React.ReactNode;
    percentage: number;
    record?: string;
  };
  team2: {
    id: string;
    name: string;
    logo?: React.ReactNode;
    percentage: number;
    record?: string;
  };
  volume: string;
  league: string;
  dateTime: string;
  score?: {
    team1: number;
    team2: number;
  };
  status?: 'live' | 'final' | 'upcoming';
}

export function SportsMarketDetails({
  marketId,
  team1,
  team2,
  volume,
  league,
  dateTime,
  score,
  status = 'final',
}: SportsMarketDetailsProps) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showHolders, setShowHolders] = useState(false);
  const { t } = useT();

  // Calculate draw percentage (100 - team1 - team2)
  const drawPercentage = Math.max(0, 100 - team1.percentage - team2.percentage);
  const team1Price = (team1.percentage / 100).toFixed(0);
  const drawPrice = (drawPercentage / 100).toFixed(0);
  const team2Price = (team2.percentage / 100).toFixed(0);

  function handleBack() {
    router.push('/sports');
  }

  function handleTabChange(_: React.SyntheticEvent, newValue: number) {
    setActiveTab(newValue);
  }

  function handlePostComment() {
    if (commentText.trim()) {
      // TODO: Implement comment posting
      setCommentText('');
    }
  }

  const [tradeAmount, setTradeAmount] = useState('0');
  const [tradeTab, setTradeTab] = useState<'buy' | 'sell'>('buy');
  const [tradeSide, setTradeSide] = useState<'yes' | 'no'>('yes');
  const balance = 0; // TODO: Get from user state

  function handleQuickAmount(value: number | string) {
    if (value === 'Max') {
      setTradeAmount(balance.toString());
    } else {
      setTradeAmount((prev) =>
        (
          parseFloat(prev || '0') + (typeof value === 'string' ? parseFloat(value) : value)
        ).toString()
      );
    }
  }

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBack} size="small" sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ cursor: 'pointer' }}
            onClick={handleBack}
          >
            Back to {league}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <CodeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <BookmarkBorderIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 380px', xl: '1fr 420px' },
          gap: { xs: 2, sm: 3 },
          alignItems: 'start',
        }}
      >
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Game Overview Card */}
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {/* Action Icons - Top Right */}
            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 0.5 }}>
              <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
                <CodeIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
                <BookmarkBorderIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Box>

            <CardContent sx={{ p: 3 }}>
              {/* Event Time */}
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ textAlign: 'center', mb: 3, color: 'text.primary' }}
              >
                {dateTime.includes('PM') || dateTime.includes('AM')
                  ? dateTime.split(' ').pop()
                  : dateTime}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                {/* Team 1 */}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}
                >
                  <Box sx={{ mb: 1.5 }}>{team1.logo}</Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ mb: 0.5, textAlign: 'center' }}
                  >
                    {team1.name}
                  </Typography>
                  {team1.record && (
                    <Typography variant="caption" color="text.secondary">
                      {team1.record}
                    </Typography>
                  )}
                </Box>

                {/* Team 2 */}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}
                >
                  <Box sx={{ mb: 1.5 }}>{team2.logo}</Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ mb: 0.5, textAlign: 'center' }}
                  >
                    {team2.name}
                  </Typography>
                  {team2.record && (
                    <Typography variant="caption" color="text.secondary">
                      {team2.record}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Probability Bar */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    height: 32,
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: `${team1.percentage}%`,
                      bgcolor: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                  <Box
                    sx={{
                      width: `${drawPercentage}%`,
                      bgcolor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                  <Box
                    sx={{
                      width: `${team2.percentage}%`,
                      bgcolor: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {volume} Vol.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Porvenix
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Moneyline Section */}
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Moneyline
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {volume} Vol.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: 3,
                    bgcolor: '#2563eb',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#1d4ed8',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {team1.name.split(' ')[0]} {team1Price}¢
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: 3,
                    borderColor: 'divider',
                    bgcolor: '#e5e7eb',
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#d1d5db',
                      borderColor: 'divider',
                    },
                  }}
                >
                  {t('sports.draw', { defaultValue: 'Draw' }).toUpperCase()} {drawPrice}¢
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: 3,
                    borderColor: 'divider',
                    bgcolor: '#374151',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#4b5563',
                      borderColor: 'divider',
                    },
                  }}
                >
                  {team2.name.split(' ')[0]} {team2Price}¢
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Comments/Holders/Activity Tabs */}
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 2,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                  },
                  '& .Mui-selected': {
                    color: '#2563eb',
                    fontWeight: 600,
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: '#2563eb',
                  },
                }}
              >
                <Tab label="Comments" />
                <Tab label="Holders" />
                <Tab label="Activity" />
              </Tabs>
            </Box>
            <CardContent sx={{ p: 2 }}>
              {activeTab === 0 && (
                <Box>
                  {/* Comment Input */}
                  {/* <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Add a comment"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handlePostComment}
                      disabled={!commentText.trim()}
                      sx={{
                        bgcolor: '#2563eb',
                        color: '#fff',
                        px: 3,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: '#1d4ed8',
                        },
                        '&:disabled': {
                          bgcolor: '#e5e7eb',
                          color: '#9ca3af',
                        },
                      }}
                    >
                      Post
                    </Button>
                  </Box> */}

                  {/* Sorting Options */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        sx={{
                          borderRadius: 2,
                          fontSize: '0.875rem',
                        }}
                      >
                        <MenuItem value="newest">Newest</MenuItem>
                        <MenuItem value="oldest">Oldest</MenuItem>
                        <MenuItem value="popular">Popular</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showHolders}
                          onChange={(e) => setShowHolders(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Holders
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Warning Message */}
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.75rem' }}>
                      Beware of external links.
                    </Typography>
                  </Box>

                  {/* Comments List */}
                  <ComponentErrorBoundary componentName="Comments Section">
                    <CommentsSection marketId={marketId} />
                  </ComponentErrorBoundary>
                </Box>
              )}
              {activeTab === 1 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Holders information coming soon
                  </Typography>
                </Box>
              )}
              {activeTab === 2 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Activity feed coming soon
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right Column - Trading Sidebar */}
        <Box
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 80 },
            order: { xs: -1, lg: 0 },
          }}
        >
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Market Header */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {team1.logo}
                  <Typography variant="h6" fontWeight={600}>
                    {team1.name.split(' ')[0]} vs {team2.name.split(' ')[0]}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: '#ef4444',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {team1.name.split(' ')[0]}
                </Box>
              </Box>

              {/* Buy/Sell Tabs */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant={tradeTab === 'buy' ? 'contained' : 'outlined'}
                  onClick={() => setTradeTab('buy')}
                  sx={{
                    bgcolor: tradeTab === 'buy' ? '#2563eb' : 'transparent',
                    color: tradeTab === 'buy' ? '#fff' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeTab === 'buy' ? '#1d4ed8' : 'action.hover',
                    },
                  }}
                >
                  Buy
                </Button>
                <Button
                  fullWidth
                  variant={tradeTab === 'sell' ? 'contained' : 'outlined'}
                  onClick={() => setTradeTab('sell')}
                  sx={{
                    bgcolor: tradeTab === 'sell' ? '#2563eb' : 'transparent',
                    color: tradeTab === 'sell' ? '#fff' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeTab === 'sell' ? '#1d4ed8' : 'action.hover',
                    },
                  }}
                >
                  Sell
                </Button>
              </Box>

              {/* Market Dropdown */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value="moneyline"
                  sx={{
                    borderRadius: 2,
                    fontSize: '0.875rem',
                  }}
                >
                  <MenuItem value="moneyline">Market ✓</MenuItem>
                </Select>
              </FormControl>

              {/* Yes/No Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant={tradeSide === 'yes' ? 'contained' : 'outlined'}
                  onClick={() => setTradeSide('yes')}
                  sx={{
                    bgcolor: tradeSide === 'yes' ? '#dcfce7' : 'transparent',
                    color: tradeSide === 'yes' ? '#166534' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeSide === 'yes' ? '#bbf7d0' : 'action.hover',
                    },
                  }}
                >
                  Yes {team1Price}¢
                </Button>
                <Button
                  fullWidth
                  variant={tradeSide === 'no' ? 'contained' : 'outlined'}
                  onClick={() => setTradeSide('no')}
                  sx={{
                    bgcolor: tradeSide === 'no' ? '#fee2e2' : 'transparent',
                    color: tradeSide === 'no' ? '#991b1b' : 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: tradeSide === 'no' ? '#fecaca' : 'action.hover',
                    },
                  }}
                >
                  No {100 - parseInt(team1Price)}¢
                </Button>
              </Box>

              {/* Amount Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Amount
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Balance ${balance.toFixed(2)}
                </Typography>
                <TextField
                  fullWidth
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                  placeholder="$0"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      borderRadius: 2,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(1)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$1
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(20)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$20
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount(100)}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    +$100
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickAmount('Max')}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }}
                  >
                    Max
                  </Button>
                </Box>
              </Box>

              {/* Deposit Button */}
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#2563eb',
                  color: '#fff',
                  py: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                  },
                }}
              >
                Deposit
              </Button>

              {/* Terms of Use */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center' }}
              >
                By trading, you agree to the{' '}
                <Link href="#" sx={{ color: '#2563eb', textDecoration: 'none' }}>
                  Terms of Use
                </Link>
                .
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Trade Modal */}
      {/* Trade Modal - Commented out due to missing state and props
      {tradeModalState.outcome && isDesktop && (
        <TradeModal
          isOpen={tradeModalState.isOpen}
          market={marketForModal}
          outcome={tradeModalState.outcome}
          isBuyOrder={tradeModalState.isBuyOrder}
          onClose={handleTradeModalClose}
          teamColor={tradeModalState.teamColor}
        />
      )} 
      */}
    </Box>
  );
}

export default SportsMarketDetails;
